import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';

const ConfirmSubscriptionPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const type = searchParams.get('type'); // 'waitlist' or null (legacy GraphQL)

  useEffect(() => {
    const confirmSubscription = async () => {
      // ─── Waitlist confirmation (REST API) ───
      if (type === 'waitlist') {
        if (!token) {
          setStatus('error');
          setMessage('Invalid confirmation link.');
          return;
        }

        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1';
          const response = await fetch(`${apiUrl}/waitlist/confirm?token=${encodeURIComponent(token)}`);
          const result = await response.json();

          if (result.success) {
            if (result.message && result.message.includes('already')) {
              setStatus('already_confirmed');
              setMessage('Your email is already confirmed. You are on the waitlist!');
            } else {
              setStatus('success');
              setMessage('Your email has been confirmed. You are on the waitlist!');
            }
          } else {
            setStatus('error');
            setMessage(result.error || 'Confirmation failed. The link may have expired.');
          }
        } catch (error) {
          console.error('Waitlist confirmation error:', error);
          setStatus('error');
          setMessage('Something went wrong during confirmation. Please try again.');
        }
        return;
      }

      // ─── Legacy GraphQL confirmation ───
      if (!token || !email) {
        setStatus('error');
        setMessage('Invalid confirmation link.');
        return;
      }

      try {
        const response = await fetch(process.env.REACT_APP_GRAPHQL_ENDPOINT || 'https://jusgmj3durgmdnklgyv2cbo57q.appsync-api.eu-north-1.amazonaws.com/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_GRAPHQL_API_KEY || 'da2-4xxr6ke64rbipdtohlzbhfhwj4',
          },
          body: JSON.stringify({
            query: `
              query GetSubscriberByEmail($email: String!) {
                subscriberByEmail(email: $email) {
                  items {
                    id
                    email
                    status
                    confirmationToken
                  }
                }
              }
            `,
            variables: { email: decodeURIComponent(email) },
          }),
        });

        const result = await response.json();
        const subscribers = result.data?.subscriberByEmail?.items || [];

        if (subscribers.length === 0) {
          setStatus('error');
          setMessage('Subscription record not found. Please subscribe again.');
          return;
        }

        const subscriber = subscribers[0];

        if (subscriber.status === 'CONFIRMED') {
          setStatus('already_confirmed');
          setMessage('Your subscription is already active and confirmed!');
          return;
        }

        const updateResponse = await fetch(process.env.REACT_APP_GRAPHQL_ENDPOINT || 'https://jusgmj3durgmdnklgyv2cbo57q.appsync-api.eu-north-1.amazonaws.com/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_GRAPHQL_API_KEY || 'da2-4xxr6ke64rbipdtohlzbhfhwj4',
          },
          body: JSON.stringify({
            query: `
              mutation UpdateSubscriber($input: UpdateFlowSubscriberInput!) {
                updateFlowSubscriber(input: $input) {
                  id
                  email
                  status
                  confirmedAt
                }
              }
            `,
            variables: {
              input: {
                id: subscriber.id,
                status: 'CONFIRMED',
                confirmedAt: new Date().toISOString(),
              },
            },
          }),
        });

        const updateResult = await updateResponse.json();

        if (updateResult.errors) {
          throw new Error(updateResult.errors[0]?.message || 'Confirmation failed');
        }

        setStatus('success');
        setMessage('Your subscription has been confirmed!');

      } catch (error) {
        console.error('Confirmation error:', error);
        setStatus('error');
        setMessage('Something went wrong during confirmation. Please try again.');
      }
    };

    confirmSubscription();
  }, [token, email, type]);

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased flex flex-col justify-between">
      <div>
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center justify-between h-20">
              <Logo />
              <div className="hidden lg:flex items-center gap-6">
                <Link to="/products" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Platform</Link>
                <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Pricing</Link>
                <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>
                <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
                <Link to="/csr" className="font-mono text-xs uppercase tracking-[0.15em] text-green-600 hover:text-green-700 transition-colors">CSR</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Content Box */}
        <div className="pt-36 pb-20 px-6 max-w-lg mx-auto">
          <div className="bg-white border border-zinc-300 p-8 md:p-10 rounded-none text-center">
            
            {status === 'loading' && (
              <>
                <div className="w-12 h-12 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-6 h-6 text-black animate-spin" />
                </div>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Verification</span>
                <h1 className="font-sans text-2xl font-bold text-black mb-2">Confirming email...</h1>
                <p className="font-sans text-xs text-zinc-600">Please wait while we verify your newsletter subscription token.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-12 h-12 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-6 h-6 text-black" />
                </div>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Verification Successful</span>
                <h1 className="font-sans text-2xl font-bold text-black mb-2">Subscription Confirmed!</h1>
                <p className="font-sans text-xs text-zinc-600 mb-6">{message}</p>
                <p className="font-mono text-[11px] text-zinc-500 mb-8">
                  You are now registered to receive Flows™ institutional intelligence dispatch.
                </p>
                <Link 
                  to="/" 
                  className="py-3.5 px-6 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all inline-block w-full"
                >
                  Return to Homepage
                </Link>
              </>
            )}

            {status === 'already_confirmed' && (
              <>
                <div className="w-12 h-12 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-6 h-6 text-black" />
                </div>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Notice</span>
                <h1 className="font-sans text-2xl font-bold text-black mb-2">Already Active</h1>
                <p className="font-sans text-xs text-zinc-600 mb-8">{message}</p>
                <Link 
                  to="/" 
                  className="py-3.5 px-6 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all inline-block w-full"
                >
                  Return to Homepage
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-12 h-12 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-6 h-6 text-black" />
                </div>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Verification Failed</span>
                <h1 className="font-sans text-2xl font-bold text-black mb-2">Invalid Link</h1>
                <p className="font-sans text-xs text-zinc-600 mb-8">{message}</p>
                <div className="space-y-3">
                  <Link 
                    to="/" 
                    className="py-3.5 px-6 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all inline-block w-full"
                  >
                    Return to Homepage
                  </Link>
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ConfirmSubscriptionPage;
